import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, TournamentsAPI } from "@/lib/api";
import SiteHeader from "@/components/SiteHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Upload, X, AlertTriangle, Loader2 } from "lucide-react";
import { LEAGUE_COLUMNS } from "@/lib/standings";
import { ROUND_LABELS } from "@/lib/knockout";

const norm = (s) =>
  (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim();

const inputClass =
  "bg-white/[0.04] border-white/10 text-white placeholder:text-zinc-600 h-10 rounded-md";

const KO_ROUNDS = ["qf", "sf", "final", "third", "c_sf", "c5", "c7"];

export default function AdminImportTournament() {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [url, setUrl] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [data, setData] = useState(null);
  const [existing, setExisting] = useState([]);
  const [targetId, setTargetId] = useState("new");
  const [saving, setSaving] = useState(false);

  const onFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    setFiles((f) => [...f, ...picked].slice(0, 5));
    e.target.value = "";
  };

  const analyze = async () => {
    if (!files.length && !url.trim()) {
      toast.error("Ajoutez au moins un fichier ou un lien");
      return;
    }
    setAnalyzing(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      fd.append("url", url.trim());
      const res = await api.post("/ai/import/analyze", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      const d = res.data;
      // fusion équipes par (nom, groupe) — une équipe peut exister dans 2 groupes
      const entries = [];
      const find = (name, group) =>
        entries.find(
          (e) => norm(e.name) === norm(name) && norm(e.group || "") === norm(group || ""),
        );
      (d.teams || []).forEach((t) => {
        if (t.name && !find(t.name, t.group)) entries.push({ ...t });
      });
      (d.standings || []).forEach((s) => {
        if (!s.team || find(s.team, s.group)) return;
        const bare = entries.find(
          (e) => norm(e.name) === norm(s.team) && !(e.group || "").trim(),
        );
        if (bare && s.group) {
          bare.group = s.group;
          return;
        }
        entries.push({ name: s.team, group: s.group });
      });
      d.teams = entries;
      d.standings = d.standings || [];
      d.group_matches = d.group_matches || [];
      d.knockout_matches = d.knockout_matches || [];
      d.warnings = d.warnings || [];
      setData(d);
      try {
        const list = await TournamentsAPI.list();
        setExisting(list);
      } catch {
        /* no-op */
      }
      toast.success("Document analysé — vérifiez et corrigez avant d'importer");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'analyse du document");
    } finally {
      setAnalyzing(false);
    }
  };

  const statFor = (team) => {
    const rows = (data.standings || []).filter((s) => norm(s.team) === norm(team.name));
    return (
      rows.find((s) => norm(s.group || "") === norm(team.group || "")) || rows[0] || {}
    );
  };

  const updateStat = (team, key, value) => {
    setData((d) => {
      const standings = [...(d.standings || [])];
      const rows = standings.filter((s) => norm(s.team) === norm(team.name));
      let row =
        rows.find((s) => norm(s.group || "") === norm(team.group || "")) ||
        (rows.length === 1 ? rows[0] : null);
      if (!row) {
        row = { team: team.name, group: team.group || null };
        standings.push(row);
      }
      row[key] = value === "" ? null : Number(value);
      return { ...d, standings };
    });
  };

  const buildAndSave = async () => {
    const target = targetId !== "new" ? existing.find((t) => t.id === targetId) : null;
    const existingByName = new Map(
      (target?.teams || []).map((t) => [norm(t.name), t]),
    );
    const teams = (data.teams || []).map((t) => {
      const prev = existingByName.get(norm(t.name));
      const s = statFor(t);
      const wins = Number(s.wins) || 0;
      const draws = Number(s.draws) || 0;
      const losses = Number(s.losses) || 0;
      const gf = Number(s.goals_for) || 0;
      const ga = Number(s.goals_against) || 0;
      return {
        id: prev?.id || crypto.randomUUID(),
        name: t.name,
        group: t.group || s.group || "",
        logo_url: prev?.logo_url || "",
        primary_color: prev?.primary_color || "#0066FF",
        team_id: prev?.team_id || null,
        matches_played: Number(s.matches_played) || wins + draws + losses,
        wins,
        draws,
        losses,
        goals_for: gf,
        goals_against: ga,
        goal_diff: gf - ga,
        points: s.points != null ? Number(s.points) || 0 : 3 * wins + draws,
      };
    });
    const candidates = (name) => teams.filter((t) => norm(t.name) === norm(name));
    const resolvePair = (hName, aName) => {
      const hs = candidates(hName);
      const as = candidates(aName);
      if (!hs.length || !as.length) return null;
      for (const h of hs)
        for (const a of as)
          if (norm(h.group || "") === norm(a.group || "")) return [h.id, a.id];
      return [hs[0].id, as[0].id];
    };
    const unresolved = [];
    const matches = (data.group_matches || [])
      .map((m) => {
        const pair = resolvePair(m.home, m.away);
        if (!pair) {
          unresolved.push(`${m.home || "?"} vs ${m.away || "?"}`);
          return null;
        }
        const [h, a] = pair;
        return {
          id: crypto.randomUUID(),
          home_id: h,
          away_id: a,
          home_score: m.home_score == null || m.home_score === "" ? null : Number(m.home_score),
          away_score: m.away_score == null || m.away_score === "" ? null : Number(m.away_score),
          label: "",
          date: m.date || "",
          venue: m.venue || "",
        };
      })
      .filter(Boolean);

    let knockout = target?.knockout || null;
    const koIn = data.knockout_matches || [];
    const koUnresolved = [];
    if (koIn.length) {
      const rounds = new Set(koIn.map((m) => m.round));
      const slots = {};
      knockout = {
        format: rounds.has("qf") ? "qf" : "sf",
        qualified_per_group: 2,
        third_place: rounds.has("third"),
        classification: rounds.has("c_sf") || rounds.has("c5") || rounds.has("c7"),
        tiebreaker: "diff",
        matches: koIn
          .filter((m) => KO_ROUNDS.includes(m.round))
          .map((m) => {
            slots[m.round] = (slots[m.round] || 0) + 1;
            const h = candidates(m.home)[0]?.id;
            const a = candidates(m.away)[0]?.id;
            if (m.home && !h) koUnresolved.push(m.home);
            if (m.away && !a) koUnresolved.push(m.away);
            return {
              id: crypto.randomUUID(),
              round: m.round,
              slot: slots[m.round],
              label: m.label || `${ROUND_LABELS[m.round]} ${slots[m.round]}`,
              home_source: { type: "team", team_id: h || "" },
              away_source: { type: "team", team_id: a || "" },
              home_score: m.home_score == null || m.home_score === "" ? null : Number(m.home_score),
              away_score: m.away_score == null || m.away_score === "" ? null : Number(m.away_score),
              home_pens: m.home_pens == null || m.home_pens === "" ? null : Number(m.home_pens),
              away_pens: m.away_pens == null || m.away_pens === "" ? null : Number(m.away_pens),
              date: m.date || "",
              venue: m.venue || "",
            };
          }),
      };
    }

    const payload = {
      name: target ? target.name : data.name || "Tournoi importé",
      subtitle:
        data.subtitle ||
        [data.date, data.location].filter(Boolean).join(" · ") ||
        target?.subtitle ||
        "",
      season: data.season || target?.season || "",
      sport: target?.sport || "football",
      standings_format: "league",
      teams,
      matches,
      knockout,
    };

    if (unresolved.length) {
      toast.warning(`Matchs ignorés (équipe inconnue) : ${unresolved.join(", ")}`);
    }
    if (koUnresolved.length) {
      toast.warning(
        `Équipes de phase finale introuvables (à affecter manuellement ensuite) : ${[...new Set(koUnresolved)].join(", ")}`,
      );
    }

    setSaving(true);
    try {
      const result = target
        ? await TournamentsAPI.update(target.id, payload)
        : await TournamentsAPI.create(payload);
      toast.success(target ? "Tournoi complété" : "Tournoi créé depuis le document");
      navigate(`/admin/tournaments/${result.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const confirmSave = () => {
    if (targetId !== "new") {
      const t = existing.find((x) => x.id === targetId);
      if (
        !window.confirm(
          `Compléter « ${t?.name} » remplacera ses équipes, classements, matchs et phases finales par les données importées (les logos des équipes au même nom sont conservés). Continuer ?`,
        )
      )
        return;
    }
    buildAndSave();
  };

  const dupNames = (() => {
    if (!data) return new Set();
    const count = new Map();
    (data.teams || []).forEach((t) => {
      const k = norm(t.name);
      count.set(k, (count.get(k) || 0) + 1);
    });
    return new Set(
      (data.teams || []).filter((t) => count.get(norm(t.name)) > 1).map((t) => t.name),
    );
  })();

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <SiteHeader />
      <div className="max-w-4xl mx-auto w-full px-4 md:px-8 py-10 md:py-16 pb-mobile-safe">
        <Link
          to="/tournaments"
          className="inline-flex items-center gap-2 text-zinc-500 hover:text-white transition-colors text-xs uppercase tracking-[0.25em] font-body mb-6"
        >
          <ArrowLeft className="w-3 h-3" /> Tournois
        </Link>
        <div className="flex items-center gap-3 mb-2">
          <Sparkles className="w-8 h-8 text-[#D4AF37]" />
          <h1 className="font-display text-4xl md:text-6xl font-semibold uppercase tracking-tighter leading-none">
            Importer <span className="text-gradient-gold">avec l'IA</span>
          </h1>
        </div>
        <p className="text-zinc-400 font-body mb-8">
          Capture d'écran, photo, PDF ou lien de résultats → l'IA extrait équipes,
          groupes, classements, matchs et phases finales. Vous vérifiez tout avant
          l'enregistrement.
        </p>

        <div className="bg-[#111216] border border-white/10 rounded-xl p-5 md:p-6 space-y-4 mb-8">
          <input
            id="ai-files"
            type="file"
            accept="image/png,image/jpeg,image/webp,image/heic,application/pdf"
            multiple
            className="hidden"
            onChange={onFiles}
            data-testid="ai-import-files"
          />
          <label
            htmlFor="ai-files"
            className="flex flex-col items-center justify-center gap-2 border border-dashed border-white/15 rounded-lg py-8 cursor-pointer hover:border-[#D4AF37]/50 transition-colors"
          >
            <Upload className="w-6 h-6 text-zinc-500" />
            <span className="text-sm text-zinc-400 font-body">
              Captures d'écran, photos ou PDF (max 5 fichiers, 10 Mo chacun)
            </span>
          </label>
          {files.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {files.map((f, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-full px-3 py-1 text-xs text-zinc-300"
                >
                  {f.name}
                  <button
                    type="button"
                    onClick={() => setFiles(files.filter((_, idx) => idx !== i))}
                    className="text-zinc-500 hover:text-red-400"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 block mb-1">
              Ou lien vers une page de résultats (facultatif)
            </Label>
            <Input
              className={inputClass}
              placeholder="https://..."
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              data-testid="ai-import-url"
            />
          </div>
          <Button
            type="button"
            onClick={analyze}
            disabled={analyzing}
            data-testid="ai-import-analyze"
            className="w-full text-black rounded-md h-11 font-body font-medium hover:opacity-90"
            style={{ backgroundColor: "#D4AF37" }}
          >
            {analyzing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyse en cours (jusqu'à 1 min)...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" /> Analyser avec l'IA
              </>
            )}
          </Button>
        </div>

        {data && (
          <div className="space-y-8" data-testid="ai-import-preview">
            {data.warnings?.length > 0 && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                <div className="flex items-center gap-2 text-amber-400 font-display uppercase tracking-wide text-sm font-bold mb-2">
                  <AlertTriangle className="w-4 h-4" /> À vérifier
                </div>
                <ul className="text-xs text-amber-200/80 font-body space-y-1 list-disc pl-4">
                  {data.warnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="grid sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 block mb-1">
                  Nom du tournoi
                </Label>
                <Input
                  className={inputClass}
                  value={data.name || ""}
                  onChange={(e) => setData({ ...data, name: e.target.value })}
                  data-testid="ai-name"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 block mb-1">
                  Saison
                </Label>
                <Input
                  className={inputClass}
                  value={data.season || ""}
                  onChange={(e) => setData({ ...data, season: e.target.value })}
                />
              </div>
              <div className="sm:col-span-3">
                <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 block mb-1">
                  Sous-titre (date · lieu)
                </Label>
                <Input
                  className={inputClass}
                  value={
                    data.subtitle ??
                    [data.date, data.location].filter(Boolean).join(" · ")
                  }
                  onChange={(e) => setData({ ...data, subtitle: e.target.value, date: null, location: null })}
                />
              </div>
            </div>

            <div>
              <h3 className="font-display text-lg font-bold uppercase tracking-tight mb-3">
                Équipes & classement ({data.teams.length})
              </h3>
              <div className="space-y-2">
                {data.teams.map((t, i) => {
                  const s = statFor(t);
                  return (
                    <div
                      key={i}
                      className={`bg-[#111216] border rounded-lg p-3 space-y-2 ${dupNames.has(t.name) ? "border-amber-500/50" : "border-white/10"}`}
                    >
                      <div className="grid grid-cols-[1fr,1fr,auto] gap-2">
                        <Input
                          className={`${inputClass} h-9 text-sm`}
                          value={t.name}
                          onChange={(e) => {
                            const teams = [...data.teams];
                            teams[i] = { ...t, name: e.target.value };
                            setData({ ...data, teams });
                          }}
                          data-testid={`ai-team-name-${i}`}
                        />
                        <Input
                          className={`${inputClass} h-9 text-sm`}
                          placeholder="Groupe (facultatif)"
                          value={t.group || ""}
                          onChange={(e) => {
                            const teams = [...data.teams];
                            teams[i] = { ...t, group: e.target.value };
                            setData({ ...data, teams });
                          }}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          data-testid={`ai-team-remove-${i}`}
                          onClick={() =>
                            setData({ ...data, teams: data.teams.filter((_, idx) => idx !== i) })
                          }
                          className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10 h-9 w-9 p-0"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-7 gap-1.5">
                        {LEAGUE_COLUMNS.filter((c) => c.key !== "goal_diff").map(({ key, label }) => (
                          <div key={key}>
                            <div className="text-[9px] uppercase tracking-[0.15em] text-zinc-500 text-center">
                              {label}
                            </div>
                            <Input
                              type="number"
                              className={`${inputClass} h-8 px-0.5 text-center text-xs`}
                              value={s[key] ?? ""}
                              placeholder="-"
                              onChange={(e) => updateStat(t, key, e.target.value)}
                            />
                          </div>
                        ))}
                      </div>
                      {dupNames.has(t.name) && (
                        <div className="text-[11px] text-amber-400 font-body">
                          Présente dans plusieurs groupes — vérifiez qu'il ne s'agit pas d'un doublon
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {data.group_matches.length > 0 && (
              <div>
                <h3 className="font-display text-lg font-bold uppercase tracking-tight mb-3">
                  Matchs de groupes ({data.group_matches.length})
                </h3>
                <div className="space-y-2">
                  {data.group_matches.map((m, i) => (
                    <div
                      key={i}
                      className="grid grid-cols-[1fr,44px,8px,44px,1fr,auto] gap-1.5 items-center bg-[#111216] border border-white/10 rounded-lg p-2"
                    >
                      <Input
                        className={`${inputClass} h-8 text-xs`}
                        value={m.home || ""}
                        onChange={(e) => {
                          const gm = [...data.group_matches];
                          gm[i] = { ...m, home: e.target.value };
                          setData({ ...data, group_matches: gm });
                        }}
                      />
                      <Input
                        type="number"
                        className={`${inputClass} h-8 px-0.5 text-center text-xs`}
                        value={m.home_score ?? ""}
                        placeholder="-"
                        onChange={(e) => {
                          const gm = [...data.group_matches];
                          gm[i] = { ...m, home_score: e.target.value };
                          setData({ ...data, group_matches: gm });
                        }}
                      />
                      <div className="text-center text-zinc-600 text-xs">–</div>
                      <Input
                        type="number"
                        className={`${inputClass} h-8 px-0.5 text-center text-xs`}
                        value={m.away_score ?? ""}
                        placeholder="-"
                        onChange={(e) => {
                          const gm = [...data.group_matches];
                          gm[i] = { ...m, away_score: e.target.value };
                          setData({ ...data, group_matches: gm });
                        }}
                      />
                      <Input
                        className={`${inputClass} h-8 text-xs`}
                        value={m.away || ""}
                        onChange={(e) => {
                          const gm = [...data.group_matches];
                          gm[i] = { ...m, away: e.target.value };
                          setData({ ...data, group_matches: gm });
                        }}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() =>
                          setData({
                            ...data,
                            group_matches: data.group_matches.filter((_, idx) => idx !== i),
                          })
                        }
                        className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10 h-8 w-8 p-0"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.knockout_matches.length > 0 && (
              <div>
                <h3 className="font-display text-lg font-bold uppercase tracking-tight mb-3">
                  Phase à éliminations ({data.knockout_matches.length})
                </h3>
                <div className="space-y-2">
                  {data.knockout_matches.map((m, i) => (
                    <div
                      key={i}
                      className="bg-[#111216] border border-white/10 rounded-lg p-2 space-y-1.5"
                    >
                      <div className="grid grid-cols-[1fr,44px,8px,44px,1fr,auto] gap-1.5 items-center">
                        <Input
                          className={`${inputClass} h-8 text-xs`}
                          value={m.home || ""}
                          placeholder="À déterminer"
                          onChange={(e) => {
                            const km = [...data.knockout_matches];
                            km[i] = { ...m, home: e.target.value };
                            setData({ ...data, knockout_matches: km });
                          }}
                        />
                        <Input
                          type="number"
                          className={`${inputClass} h-8 px-0.5 text-center text-xs`}
                          value={m.home_score ?? ""}
                          placeholder="-"
                          onChange={(e) => {
                            const km = [...data.knockout_matches];
                            km[i] = { ...m, home_score: e.target.value };
                            setData({ ...data, knockout_matches: km });
                          }}
                        />
                        <div className="text-center text-zinc-600 text-xs">–</div>
                        <Input
                          type="number"
                          className={`${inputClass} h-8 px-0.5 text-center text-xs`}
                          value={m.away_score ?? ""}
                          placeholder="-"
                          onChange={(e) => {
                            const km = [...data.knockout_matches];
                            km[i] = { ...m, away_score: e.target.value };
                            setData({ ...data, knockout_matches: km });
                          }}
                        />
                        <Input
                          className={`${inputClass} h-8 text-xs`}
                          value={m.away || ""}
                          placeholder="À déterminer"
                          onChange={(e) => {
                            const km = [...data.knockout_matches];
                            km[i] = { ...m, away: e.target.value };
                            setData({ ...data, knockout_matches: km });
                          }}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() =>
                            setData({
                              ...data,
                              knockout_matches: data.knockout_matches.filter((_, idx) => idx !== i),
                            })
                          }
                          className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10 h-8 w-8 p-0"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                      <Select
                        value={m.round}
                        onValueChange={(v) => {
                          const km = [...data.knockout_matches];
                          km[i] = { ...m, round: v };
                          setData({ ...data, knockout_matches: km });
                        }}
                      >
                        <SelectTrigger className={`${inputClass} h-8 text-xs w-56`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#111216] border-white/10 text-white">
                          {KO_ROUNDS.map((r) => (
                            <SelectItem key={r} value={r}>
                              {ROUND_LABELS[r]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-[#111216] border border-white/10 rounded-xl p-5 space-y-3">
              <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 block">
                Destination
              </Label>
              <Select value={targetId} onValueChange={setTargetId}>
                <SelectTrigger className={inputClass} data-testid="ai-import-target">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#111216] border-white/10 text-white">
                  <SelectItem value="new">Créer un nouveau tournoi</SelectItem>
                  {existing.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      Compléter : {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                type="button"
                onClick={confirmSave}
                disabled={saving || !data.teams.length}
                data-testid="ai-import-save"
                className="w-full text-black rounded-md h-11 font-body font-medium hover:opacity-90"
                style={{ backgroundColor: "#D4AF37" }}
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                Confirmer l'importation
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
